import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import { CircularProgress } from '@mui/material';
import { UserGroupIcon, ChevronRightIcon } from '@heroicons/react/24/solid';

const NewChat = () => {
  const navigate = useNavigate();

  const [closeFriends, setCloseFriends] = useState([]);
  const [loadingCloseFriends, setLoadingCloseFriends] = useState(true);

  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  useEffect(() => {
    const fetchCloseFriends = async () => {
      try {
        setLoadingCloseFriends(true);
        const response = await axiosInstance.get('/general/close_friends');
        const received = response.data.users || [];
        setCloseFriends(received);
      } catch (error) {
        console.error('Failed to load close friends:', error);
      } finally {
        setLoadingCloseFriends(false);
      }
    };

    const fetchSuggested = async () => {
      try {
        setLoadingUsers(true);
        const response = await axiosInstance.get('/general/suggested');
        const received = response.data.users || [];
        setAllUsers(received);
      } catch (error) {
        console.error('Failed to load suggested users:', error);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchCloseFriends();
    fetchSuggested();
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setHasSearched(false);
      setSearchResults([]);
      setError(null);
      setLoading(false);
    }
  }, [searchQuery]);

  const suggestedUsers = allUsers.filter(
    user => !closeFriends.find(cf => cf.user_id === user.user_id)
  );

  const handleUserClick = async (user) => {
    try {
      const res = await axiosInstance.get(`/chat/find_chat_room/${user.user_id}`);
      const existingChatId = res.data.chatId;

      if (existingChatId) {
        navigate(`/chat/${existingChatId}`, {
          state: {
            chatInfo: {
              is_group: false,
              chat_name: user.username,
              profile_url: user.profile_url,
            }
          }
        });
      }
      else {
        navigate(`/chat/undefined`, {
          state: {
            chatInfo: {
              is_group: false,
              chat_name: user.username,
              profile_url: user.profile_url,
              user_id: user.user_id,
            },
          },
        });
      }
    } catch (err) {
      console.error('Failed to check chat:', err);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery) return;

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const response = await axiosInstance.get(`/search/users/?query=${searchQuery}`);
      setSearchResults(response.data.users || []);
    } catch (err) {
      console.error("Search error:", err);
      setError("Failed to fetch search results.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tailwind bg-white dark:bg-darkBackground min-h-screen text-black dark:text-white transition-colors">
      <div className="max-w-xl mx-auto p-4 mt-8">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate(-1)}
            className="text-chatBlueColor text-base"
          >
            Cancel
          </button>
          <h1 className="ext-lg text-lg font-semibold">New message</h1>
          <div className="w-10" />
        </div>

        <div className="mb-4 border-b border-gray-200 dark:border-neutral-800 pb-2 flex items-center">
          <span className="text-gray-500 dark:text-gray-300 mr-2">To:</span>
          <form onSubmit={handleSearch} className="flex-1">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full border-none items-center focus:outline-none p-0 pt-[2px] bg-transparent text-black dark:text-white"
            />
          </form>
        </div>

        <div
          className="flex items-center justify-between p-3 rounded-xl bg-gray-100 dark:bg-neutral-800 border-none cursor-pointer mb-4"
          onClick={() => navigate('/create_chat')}
        >
          <div className="flex items-center gap-3">
            <UserGroupIcon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            <span className="font-medium text-gray-800 dark:text-gray-100">Create a new group</span>
          </div>
          <ChevronRightIcon className="w-4 h-4 text-gray-400 dark:text-gray-300" />
        </div>

        {hasSearched ? (
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-300 mb-3">
              Search results
            </h2>
            <div className="divide-y divide-gray-200 dark:divide-neutral-800">
              {loading ? (
                <div className="text-center py-3 flex justify-center">
                  <CircularProgress size={20} sx={{ color: '#d1d5db' }} />
                </div>
              ) : error ? (
                <div className="py-3 text-sm text-center text-red-500 dark:text-red-400">{error}</div>
              ) : searchResults.length > 0 ? (
                searchResults.map(user => (
                  <div
                    key={user.user_id}
                    className={`flex items-center gap-3 cursor-pointer py-3 first:pt-0 last:pb-0`}
                    onClick={() => handleUserClick(user)}
                  >
                    <img
                      src={user.profile_url || '/assets/images/default_profile.png'}
                      alt={user.username}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <span className="text-gray-800 dark:text-white">{user.username}</span>
                  </div>
                ))
              ) : (
                <div className="py-3 text-sm text-center text-gray-500 dark:text-gray-300">No results found</div>
              )}
            </div>
          </div>
        ) : ( 
        (loadingCloseFriends || closeFriends.length > 0) && (
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-300 mb-3">
              Close Friends
            </h2>
            <div className="divide-y divide-gray-200 dark:divide-neutral-800">
              {loadingCloseFriends ? (
                <div className="text-center py-3 flex justify-center">
                  <CircularProgress size={20} sx={{ color: '#d1d5db' }} />
                </div>
              ) : (
                closeFriends.map(user => (
                  <div
                    key={user.user_id}
                    className={`flex items-center gap-3 ${loadingCloseFriends ? 'opacity-60 pointer-events-none' : 'cursor-pointer'} py-3 first:pt-0 last:pb-0`}
                    onClick={() => !loadingCloseFriends && handleUserClick(user)}
                  >
                    <img
                      src={user.profile_url || '/assets/images/default_profile.png'}
                      alt={user.username}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <span className="text-gray-800 dark:text-white">{user.username}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        ))}

        {!hasSearched && (
          <div>
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-300 mb-3">
              Suggested
            </h2>
            <div className="divide-y divide-gray-200 dark:divide-neutral-800">
              {loadingUsers && suggestedUsers.length === 0 ? (
                <div className="text-center py-3 flex justify-center">
                  <CircularProgress size={20} sx={{ color: '#d1d5db' }} />
                </div>
              ) : suggestedUsers.length > 0 ? (
                suggestedUsers.map(user => (
                  <div
                    key={user.user_id}
                    className={`flex items-center gap-3 ${loadingUsers ? 'opacity-60 pointer-events-none' : 'cursor-pointer'} py-3 first:pt-0 last:pb-0`}
                    onClick={() => !loadingUsers && handleUserClick(user)}
                  >
                    <img
                      src={user.profile_url || '/assets/images/default_profile.png'}
                      alt={user.username}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <span className="text-gray-800 dark:text-white">{user.username}</span>
                  </div>
                ))
              ) : (
                <div className="py-3 text-sm text-center text-gray-500 dark:text-gray-300">No suggestions available</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewChat;